import * as React from 'react';

import { Translate } from 'react-localize-redux';

import { InvoiceStatus } from '@services/payment/types/invoice-status';

import styled, { useTheme } from 'styled-components';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';

interface InvoiceStatusProps {
  invoiceStatus: InvoiceStatus;
}

interface InvoiceItemCtrlProps extends InvoiceStatusProps {
  className?: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceUrl: string;
}

const getInvoiceStatus = (invoiceStatus: InvoiceStatus) => {
  if (invoiceStatus === 'sent') {
    return <Translate id="ARTIST_PROFILE.BILLING.INVOICE.SENT" />;
  } else if (invoiceStatus === 'paid') {
    return <Translate id="ARTIST_PROFILE.BILLING.INVOICE.PAID" />;
  } else if (invoiceStatus === 'overdue') {
    return <Translate id="ARTIST_PROFILE.BILLING.INVOICE.OVERDUE" />;
  } else if (invoiceStatus === 'refund') {
    return <Translate id="ARTIST_PROFILE.BILLING.INVOICE.REFUND" />;
  } else {
    return '';
  }
};

export const InvoiceItemCtrl: React.FC<InvoiceItemCtrlProps> = (props) => {
  const handleClick = (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (!props.invoiceUrl) {
      ev.stopPropagation();
      ev.preventDefault();
      return false;
    }
  };

  const theme = useTheme();

  const getBgColor = (invoiceStatus: InvoiceStatus) => {
    if (invoiceStatus === 'sent') {
      return theme.colors.secondary;
    } else if (invoiceStatus === 'paid') {
      return theme.colors.successDark;
    } else if (invoiceStatus === 'refund') {
      return theme.colors.hint;
    } else {
      return theme.colors.accent;
    }
  };

  const getTextColor = (invoiceStatus: InvoiceStatus) => {
    if (invoiceStatus === 'sent') {
      return theme.colors.primary;
    } else if (invoiceStatus === 'paid') {
      return theme.colors.successText;
    } else if (invoiceStatus === 'refund') {
      return theme.colors.hintText;
    } else {
      return theme.colors.accent;
    }
  };

  const InvoiceStatusMarker = styled.span<InvoiceStatusProps>`
    padding: 12px 20px;
    border-radius: 2px;
    margin-left: 10px;
    background: ${(props) => getBgColor(props.invoiceStatus)};
  `;

  return (
    <div className={props.className}>
      <a
        href={props.invoiceUrl}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Box small className="box">
          <Flex justifyContent="space-between" alignItems="center">
            <div>
              <Typography weight="700">{props.invoiceNumber}</Typography>
              <Typography weight="700" opacity=".5">
                {props.invoiceDate}
              </Typography>
            </div>
            <InvoiceStatusMarker invoiceStatus={props.invoiceStatus}>
              <Typography
                color={getTextColor(props.invoiceStatus)}
                uppercase
                weight="700"
              >
                {getInvoiceStatus(props.invoiceStatus)}
              </Typography>
            </InvoiceStatusMarker>
          </Flex>
        </Box>
      </a>
    </div>
  );
};

export default styled(InvoiceItemCtrl)`
  cursor: pointer;

  &:hover {
    .box {
      background-color: ${({ theme }) => theme.colors.header};
    }
  }

  .box {
    transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }
`;
